import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Radiation, Plus, Download, AlertTriangle, Settings, Check, X, FileWarning, CheckCircle2, ChevronRight, Trash2 } from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INDIGO = '#4338CA';
const GREEN = '#16A34A';
const RED = '#DC2626';
const AMBER = '#D97706';

function monthKey(d = new Date()) {
  return d.toISOString().slice(0, 7);
}

export default function RadiationAuditPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';

  const [tab, setTab] = useState('dashboard');
  const [month, setMonth] = useState(monthKey());
  const [drls, setDrls] = useState([]);
  const [dashboard, setDashboard] = useState({ rows: [], total_cases: 0, total_outliers: 0 });
  const [cases, setCases] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [showDrlModal, setShowDrlModal] = useState(false);
  const [showIncidentModal, setShowIncidentModal] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, dash, c, inc] = await Promise.all([
        fetch(`${API}/radiation/drls`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/radiation/dashboard?month=${month}`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/radiation/cases?month=${month}`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/radiation/incidents`, { credentials: 'include' }).then(r => r.json()),
      ]);
      setDrls(d || []);
      setDashboard(dash || { rows: [], total_cases: 0, total_outliers: 0 });
      setCases(c || []);
      setIncidents(inc || []);
    } catch (e) {
      console.error('radiation load failed', e);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="radiation-page">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/sops')} data-testid="rad-back" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to SOPs
          </button>
          <a
            href={`${API}/radiation/cases/export.csv?month=${month}`}
            data-testid="rad-export-csv"
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg text-white"
            style={{ backgroundColor: INDIGO }}
          >
            <Download className="w-3.5 h-3.5" /> Export CSV ({month})
          </a>
        </div>

        {/* Cover */}
        <div className="rounded-2xl border p-6 mb-6" style={{ borderColor: `${INDIGO}33`, background: `linear-gradient(135deg, ${INDIGO}0F, transparent 60%)` }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${INDIGO}15`, color: INDIGO }}>
              <Radiation className="w-6 h-6" />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full" style={{ backgroundColor: `${INDIGO}1A`, color: INDIGO }}>
              Live audit · DRL dashboard · Incident reporting
            </span>
          </div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-foreground tracking-tight">CT Dose Audit &amp; DRL Monitoring</h1>
          <p className="text-sm text-muted-foreground mt-1">Auto outlier flagging (&gt;20% above DRL) · monthly governance dashboard · structured radiation incident trail</p>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <Kpi label="Cases this month" value={dashboard.total_cases} tone={INDIGO} />
          <Kpi label="Outliers" value={dashboard.total_outliers} tone={dashboard.total_outliers > 0 ? RED : GREEN} />
          <Kpi label="Study types tracked" value={drls.length} tone={INDIGO} />
          <Kpi label="Open incidents" value={incidents.filter(i => i.status === 'open').length} tone={incidents.some(i => i.status === 'open') ? AMBER : GREEN} />
        </div>

        {/* Month selector + DRL settings */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-5">
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Reporting month</label>
          <input
            type="month"
            value={month}
            onChange={e => setMonth(e.target.value)}
            data-testid="rad-month"
            className="text-sm bg-card border border-border rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2"
            style={{ boxShadow: `0 0 0 0 ${INDIGO}` }}
          />
          {isPM && (
            <button
              onClick={() => setShowDrlModal(true)}
              data-testid="rad-manage-drls"
              className="sm:ml-auto inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border"
              style={{ borderColor: `${INDIGO}40`, color: INDIGO }}
            >
              <Settings className="w-3.5 h-3.5" /> Manage DRLs
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-border mb-4">
          {[
            { id: 'dashboard', label: 'DRL Dashboard' },
            { id: 'add', label: 'Add Case' },
            { id: 'cases', label: `Cases (${cases.length})` },
            { id: 'incidents', label: `Incidents (${incidents.length})` },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`rad-tab-${t.id}`}
              className="px-3 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors"
              style={tab === t.id
                ? { borderColor: INDIGO, color: INDIGO }
                : { borderColor: 'transparent', color: 'var(--muted-foreground, #64748b)' }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'dashboard' && <DashboardTab rows={dashboard.rows} month={month} />}
        {tab === 'add' && <AddCaseTab drls={drls} onSaved={load} />}
        {tab === 'cases' && <CasesTab cases={cases} drls={drls} isPM={isPM} onChange={load} />}
        {tab === 'incidents' && <IncidentsTab incidents={incidents} isPM={isPM} onNew={() => setShowIncidentModal(true)} onChange={load} />}
      </div>

      {showDrlModal && <ManageDrlsModal drls={drls} onClose={() => setShowDrlModal(false)} onSaved={load} />}
      {showIncidentModal && <NewIncidentModal drls={drls} onClose={() => setShowIncidentModal(false)} onSaved={() => { setShowIncidentModal(false); load(); }} />}
    </div>
  );
}

// -------- Sub components --------

function Kpi({ label, value, tone }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
      <p className="font-['Outfit'] text-2xl font-black" style={{ color: tone }}>{value}</p>
    </div>
  );
}

function DashboardTab({ rows, month }) {
  return (
    <div className="rounded-xl border border-border bg-card overflow-x-auto" data-testid="rad-dashboard">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground border-b border-border">
            <th className="px-3 py-2">Study</th>
            <th className="px-3 py-2 text-right">Cases</th>
            <th className="px-3 py-2 text-right">Avg CTDIvol (ref)</th>
            <th className="px-3 py-2 text-right">Avg DLP (ref)</th>
            <th className="px-3 py-2 text-right">Outliers</th>
            <th className="px-3 py-2">Label</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan="6" className="px-3 py-8 text-center text-sm text-muted-foreground">No cases yet for {month}. Add cases in the <strong>Add Case</strong> tab.</td></tr>
          )}
          {rows.map(r => (
            <tr key={r.study_code} className="border-b border-border/60 last:border-0">
              <td className="px-3 py-2 font-semibold text-foreground">{r.study_name}</td>
              <td className="px-3 py-2 text-right text-foreground">{r.cases_count}</td>
              <td className="px-3 py-2 text-right text-foreground">
                <span className={r.avg_ctdivol > r.ctdivol_ref ? 'font-bold text-red-600' : ''}>{r.avg_ctdivol}</span>
                <span className="text-muted-foreground text-xs ml-1">({r.ctdivol_ref})</span>
              </td>
              <td className="px-3 py-2 text-right text-foreground">
                <span className={r.avg_dlp > r.dlp_ref ? 'font-bold text-red-600' : ''}>{r.avg_dlp}</span>
                <span className="text-muted-foreground text-xs ml-1">({r.dlp_ref})</span>
              </td>
              <td className="px-3 py-2 text-right">{r.outliers > 0 ? <span className="font-bold text-red-600">{r.outliers}</span> : r.outliers}</td>
              <td className="px-3 py-2">
                <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full"
                  style={r.label === 'Above DRL'
                    ? { backgroundColor: `${RED}15`, color: RED }
                    : r.label === 'Within DRL'
                      ? { backgroundColor: `${GREEN}15`, color: GREEN }
                      : { backgroundColor: '#6B728015', color: '#6B7280' }}>
                  {r.label === 'Above DRL' ? <AlertTriangle className="w-3 h-3" /> : r.label === 'Within DRL' ? <CheckCircle2 className="w-3 h-3" /> : null}
                  {r.label}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AddCaseTab({ drls, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ study_code: drls[0]?.study_code || '', patient_ref: '', study_date: today, ctdivol: '', dlp: '', scanner: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [last, setLast] = useState(null);

  useEffect(() => {
    if (!form.study_code && drls.length) setForm(f => ({ ...f, study_code: drls[0].study_code }));
  }, [drls, form.study_code]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await fetch(`${API}/radiation/cases`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, ctdivol: parseFloat(form.ctdivol), dlp: parseFloat(form.dlp) }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed');
      const saved = await r.json();
      setLast(saved);
      setForm({ ...form, patient_ref: '', ctdivol: '', dlp: '', scanner: form.scanner, notes: '' });
      onSaved();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-xl border border-border bg-card p-5 space-y-4" data-testid="rad-add-case-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Study">
          <select required value={form.study_code} onChange={e => setForm({ ...form, study_code: e.target.value })} data-testid="rad-field-study" className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2">
            {drls.map(d => <option key={d.study_code} value={d.study_code}>{d.study_name}</option>)}
          </select>
        </Field>
        <Field label="Study date">
          <input required type="date" value={form.study_date} onChange={e => setForm({ ...form, study_date: e.target.value })} data-testid="rad-field-date" className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2" />
        </Field>
        <Field label="Patient ref (non-identifying)">
          <input required value={form.patient_ref} onChange={e => setForm({ ...form, patient_ref: e.target.value })} data-testid="rad-field-patient" placeholder="e.g. Accession 2026-001234" className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2" />
        </Field>
        <Field label="Scanner (optional)">
          <input value={form.scanner} onChange={e => setForm({ ...form, scanner: e.target.value })} data-testid="rad-field-scanner" placeholder="e.g. Siemens Edge" className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2" />
        </Field>
        <Field label="CTDIvol (mGy)">
          <input required type="number" step="0.01" value={form.ctdivol} onChange={e => setForm({ ...form, ctdivol: e.target.value })} data-testid="rad-field-ctdi" className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2" />
        </Field>
        <Field label="DLP (mGy·cm)">
          <input required type="number" step="0.01" value={form.dlp} onChange={e => setForm({ ...form, dlp: e.target.value })} data-testid="rad-field-dlp" className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2" />
        </Field>
      </div>
      <Field label="Notes">
        <textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="rad-field-notes" className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2" />
      </Field>
      <button type="submit" disabled={saving} data-testid="rad-save-case" className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-60" style={{ backgroundColor: INDIGO }}>
        <Plus className="w-4 h-4" /> {saving ? 'Saving…' : 'Save case'}
      </button>
      {last && (
        <div className="mt-3 rounded-lg border p-3" style={{ borderColor: last.is_outlier ? RED : GREEN, backgroundColor: last.is_outlier ? `${RED}0A` : `${GREEN}0A` }}>
          <p className="text-xs font-bold uppercase tracking-wider" style={{ color: last.is_outlier ? RED : GREEN }}>
            {last.is_outlier ? '⚠ Outlier — exceeds 120% of reference' : '✓ Within reference range'}
          </p>
          <p className="text-sm mt-1 text-foreground">CTDIvol: {last.ctdivol} / {last.ctdivol_ref} ({(last.ctdi_ratio * 100).toFixed(0)}%) · DLP: {last.dlp} / {last.dlp_ref} ({(last.dlp_ratio * 100).toFixed(0)}%)</p>
        </div>
      )}
    </form>
  );
}

function CasesTab({ cases, isPM, onChange }) {
  const remove = async (id) => {
    if (!window.confirm('Delete this case?')) return;
    await fetch(`${API}/radiation/cases/${id}`, { method: 'DELETE', credentials: 'include' });
    onChange();
  };
  if (cases.length === 0) {
    return <div className="text-center py-10 text-sm text-muted-foreground" data-testid="rad-cases-empty">No cases yet for this month.</div>;
  }
  return (
    <div className="rounded-xl border border-border bg-card overflow-x-auto" data-testid="rad-cases-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground border-b border-border">
            <th className="px-3 py-2">Date</th>
            <th className="px-3 py-2">Study</th>
            <th className="px-3 py-2">Patient ref</th>
            <th className="px-3 py-2 text-right">CTDIvol</th>
            <th className="px-3 py-2 text-right">DLP</th>
            <th className="px-3 py-2">Flag</th>
            {isPM && <th className="px-3 py-2"></th>}
          </tr>
        </thead>
        <tbody>
          {cases.map(c => (
            <tr key={c.case_id} className="border-b border-border/60 last:border-0">
              <td className="px-3 py-2 text-foreground">{c.study_date}</td>
              <td className="px-3 py-2 text-foreground font-medium">{c.study_name}</td>
              <td className="px-3 py-2 text-foreground">{c.patient_ref}</td>
              <td className="px-3 py-2 text-right text-foreground">{c.ctdivol} <span className="text-xs text-muted-foreground">({(c.ctdi_ratio * 100).toFixed(0)}%)</span></td>
              <td className="px-3 py-2 text-right text-foreground">{c.dlp} <span className="text-xs text-muted-foreground">({(c.dlp_ratio * 100).toFixed(0)}%)</span></td>
              <td className="px-3 py-2">
                {c.is_outlier
                  ? <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: `${RED}15`, color: RED }}><AlertTriangle className="w-3 h-3" />OUTLIER</span>
                  : <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground">—</span>}
              </td>
              {isPM && <td className="px-3 py-2"><button onClick={() => remove(c.case_id)} data-testid={`rad-case-del-${c.case_id}`} className="text-muted-foreground hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button></td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function IncidentsTab({ incidents, isPM, onNew, onChange }) {
  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <p className="text-sm text-muted-foreground">Structured radiation incident reports — auditable trail for governance meetings.</p>
        <button onClick={onNew} data-testid="rad-new-incident" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg text-white" style={{ backgroundColor: RED }}>
          <FileWarning className="w-3.5 h-3.5" /> Report incident
        </button>
      </div>
      {incidents.length === 0 ? (
        <div className="text-center py-10 text-sm text-muted-foreground" data-testid="rad-incidents-empty">No incidents recorded. That's a good thing — keep documenting thoroughly if any occur.</div>
      ) : (
        <div className="space-y-2" data-testid="rad-incidents-list">
          {incidents.map(i => (
            <IncidentCard key={i.incident_id} incident={i} isPM={isPM} onChange={onChange} />
          ))}
        </div>
      )}
    </div>
  );
}

function IncidentCard({ incident, isPM, onChange }) {
  const [expand, setExpand] = useState(false);
  const [rc, setRc] = useState(incident.root_cause || '');
  const [ca, setCa] = useState(incident.corrective_action || '');
  const isClosed = incident.status === 'closed';
  const tone = isClosed ? GREEN : AMBER;

  const close = async () => {
    if (!rc.trim() || !ca.trim()) { alert('Root cause and corrective action are required.'); return; }
    await fetch(`${API}/radiation/incidents/${incident.incident_id}/close`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ root_cause: rc, corrective_action: ca }),
    });
    onChange();
  };

  return (
    <div className="rounded-xl border p-4" style={{ borderColor: `${tone}40` }}>
      <button onClick={() => setExpand(!expand)} className="w-full flex items-center gap-3 text-left">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${tone}15`, color: tone }}>
          {isClosed ? <CheckCircle2 className="w-4 h-4" /> : <FileWarning className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground">{incident.category} · {incident.study_type}</p>
          <p className="text-xs text-muted-foreground line-clamp-1">{incident.occurred_at.slice(0, 10)} · {incident.patient_ref} · reported by {incident.created_by_name || incident.created_by}</p>
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ backgroundColor: `${tone}15`, color: tone }}>{incident.status}</span>
        <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${expand ? 'rotate-90' : ''}`} />
      </button>
      {expand && (
        <div className="mt-3 pt-3 border-t border-border space-y-2 text-sm">
          <Row label="Description" value={incident.description} />
          {(incident.ctdivol || incident.dlp) && <Row label="Dose" value={`CTDIvol ${incident.ctdivol || '—'} · DLP ${incident.dlp || '—'}`} />}
          <Row label="Staff involved" value={incident.staff_involved} />
          <Row label="Immediate action" value={incident.immediate_action} />
          {incident.regulator_notified && <Row label="Regulator notified" value={`${incident.regulator_name || '—'} · ${incident.regulator_date || '—'}`} />}
          {isClosed && (
            <>
              <Row label="Root cause" value={incident.root_cause} />
              <Row label="Corrective action" value={incident.corrective_action} />
              <Row label="Closed by" value={`${incident.closed_by_name || incident.closed_by} · ${(incident.closed_at || '').slice(0,10)}`} />
            </>
          )}
          {!isClosed && isPM && (
            <div className="rounded-lg border border-border p-3 bg-muted/30 space-y-2">
              <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: GREEN }}>Close & sign off (PM / Admin)</p>
              <textarea rows={2} value={rc} onChange={e => setRc(e.target.value)} placeholder="Root cause analysis" className="w-full text-xs bg-background border border-border rounded px-2 py-1.5" />
              <textarea rows={2} value={ca} onChange={e => setCa(e.target.value)} placeholder="Corrective action taken" className="w-full text-xs bg-background border border-border rounded px-2 py-1.5" />
              <button onClick={close} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg text-white" style={{ backgroundColor: GREEN }}>
                <Check className="w-3 h-3" /> Close incident
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return <p><span className="uppercase tracking-wider text-[10px] font-bold text-muted-foreground mr-2">{label}</span><span className="text-foreground">{value || '—'}</span></p>;
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function ManageDrlsModal({ drls, onClose, onSaved }) {
  const [rows, setRows] = useState(drls);
  const [saving, setSaving] = useState(false);
  const save = async (r) => {
    setSaving(true);
    try {
      await fetch(`${API}/radiation/drls/${r.study_code}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ctdivol_ref: parseFloat(r.ctdivol_ref), dlp_ref: parseFloat(r.dlp_ref), source: r.source }),
      });
      onSaved();
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="rad-drl-modal">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <p className="font-['Outfit'] text-lg font-bold text-foreground">Manage Diagnostic Reference Levels</p>
            <p className="text-xs text-muted-foreground mt-0.5">Update against current ARPANSA Australian NDRL publication. Changes are logged.</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          {rows.map((r, i) => (
            <div key={r.study_code} className="rounded-xl border border-border p-3">
              <p className="text-sm font-semibold text-foreground mb-2">{r.study_name}</p>
              <div className="grid grid-cols-2 gap-2">
                <Field label="CTDIvol ref (mGy)">
                  <input type="number" step="0.1" value={r.ctdivol_ref} onChange={e => { const nr = [...rows]; nr[i] = { ...nr[i], ctdivol_ref: e.target.value }; setRows(nr); }} className="w-full text-sm bg-background border border-border rounded px-2 py-1.5" />
                </Field>
                <Field label="DLP ref (mGy·cm)">
                  <input type="number" step="1" value={r.dlp_ref} onChange={e => { const nr = [...rows]; nr[i] = { ...nr[i], dlp_ref: e.target.value }; setRows(nr); }} className="w-full text-sm bg-background border border-border rounded px-2 py-1.5" />
                </Field>
              </div>
              <Field label="Source / reference note">
                <input value={r.source} onChange={e => { const nr = [...rows]; nr[i] = { ...nr[i], source: e.target.value }; setRows(nr); }} className="w-full text-sm bg-background border border-border rounded px-2 py-1.5" />
              </Field>
              <button onClick={() => save(r)} disabled={saving} data-testid={`rad-drl-save-${r.study_code}`} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded border" style={{ borderColor: `${INDIGO}40`, color: INDIGO }}>Save</button>
              {r.updated_by && <p className="text-[11px] text-muted-foreground mt-1">Last updated: {(r.updated_at || '').slice(0,10)} by {r.updated_by}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function NewIncidentModal({ drls, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 16);
  const [f, setF] = useState({
    occurred_at: today, patient_ref: '', study_type: '', category: 'Unintended exposure',
    description: '', ctdivol: '', dlp: '', staff_involved: '', immediate_action: '',
    regulator_notified: false, regulator_name: '', regulator_date: '',
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await fetch(`${API}/radiation/incidents`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...f,
          ctdivol: f.ctdivol ? parseFloat(f.ctdivol) : null,
          dlp: f.dlp ? parseFloat(f.dlp) : null,
        }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed');
      onSaved();
    } catch (e) { alert(e.message); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-card border border-border rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="rad-incident-modal">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <p className="font-['Outfit'] text-lg font-bold text-foreground">Report Radiation Incident</p>
            <p className="text-xs text-muted-foreground mt-0.5">Structured capture for governance &amp; regulator reporting.</p>
          </div>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="When occurred"><input required type="datetime-local" value={f.occurred_at} onChange={e => setF({ ...f, occurred_at: e.target.value })} className="w-full text-sm bg-background border border-border rounded px-2 py-1.5" /></Field>
            <Field label="Category">
              <select value={f.category} onChange={e => setF({ ...f, category: e.target.value })} className="w-full text-sm bg-background border border-border rounded px-2 py-1.5">
                {['Unintended exposure', 'Protocol deviation', 'Equipment failure', 'Pregnancy screening failure', 'Wrong patient / wrong study', 'Other'].map(c => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Patient reference (non-identifying)"><input required value={f.patient_ref} onChange={e => setF({ ...f, patient_ref: e.target.value })} className="w-full text-sm bg-background border border-border rounded px-2 py-1.5" /></Field>
            <Field label="Study type"><input required value={f.study_type} onChange={e => setF({ ...f, study_type: e.target.value })} className="w-full text-sm bg-background border border-border rounded px-2 py-1.5" /></Field>
            <Field label="CTDIvol (if applicable)"><input type="number" step="0.01" value={f.ctdivol} onChange={e => setF({ ...f, ctdivol: e.target.value })} className="w-full text-sm bg-background border border-border rounded px-2 py-1.5" /></Field>
            <Field label="DLP (if applicable)"><input type="number" step="0.01" value={f.dlp} onChange={e => setF({ ...f, dlp: e.target.value })} className="w-full text-sm bg-background border border-border rounded px-2 py-1.5" /></Field>
          </div>
          <Field label="Description"><textarea required rows={3} value={f.description} onChange={e => setF({ ...f, description: e.target.value })} className="w-full text-sm bg-background border border-border rounded px-2 py-1.5" /></Field>
          <Field label="Staff involved"><input required value={f.staff_involved} onChange={e => setF({ ...f, staff_involved: e.target.value })} className="w-full text-sm bg-background border border-border rounded px-2 py-1.5" /></Field>
          <Field label="Immediate action taken"><textarea required rows={2} value={f.immediate_action} onChange={e => setF({ ...f, immediate_action: e.target.value })} className="w-full text-sm bg-background border border-border rounded px-2 py-1.5" /></Field>
          <label className="flex items-center gap-2 text-sm text-foreground"><input type="checkbox" checked={f.regulator_notified} onChange={e => setF({ ...f, regulator_notified: e.target.checked })} /> Regulator notified</label>
          {f.regulator_notified && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Regulator name"><input value={f.regulator_name} onChange={e => setF({ ...f, regulator_name: e.target.value })} placeholder="e.g. ARPANSA / State" className="w-full text-sm bg-background border border-border rounded px-2 py-1.5" /></Field>
              <Field label="Date notified"><input type="date" value={f.regulator_date} onChange={e => setF({ ...f, regulator_date: e.target.value })} className="w-full text-sm bg-background border border-border rounded px-2 py-1.5" /></Field>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-border">
          <button type="button" onClick={onClose} className="text-sm px-3 py-1.5 rounded-lg border border-border text-foreground">Cancel</button>
          <button type="submit" disabled={saving} data-testid="rad-incident-submit" className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg text-white disabled:opacity-60" style={{ backgroundColor: RED }}>
            <FileWarning className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Submit incident'}
          </button>
        </div>
      </form>
    </div>
  );
}
